import React, { memo, useCallback, useContext } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { ActionButtons } from '../../components';
import ConfirmDialogContext from '../../components/confirm_dialog_context';
import { __WEB__, COLOR, FONT, THEME } from '../../../constants/styles';
import moment from 'moment';

import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { deleteAppointmentSlotOverride } from './slice';
import { getCurrentEmployer } from '../../../selectors/employers';

const OverrideRow = memo(function OverrideRow(props) {
  function handleCancelPress() {
    props.onCancelPress(props.o);
  }

  const disabled = !props.canEdit;
  return (
    <View style={styles.eventRow}>
      <View style={[{ width: __WEB__ ? 100 : 90 }]}>
        <ActionButtons
          onInfoPress={null}
          onCancelPress={handleCancelPress}
          infoDisabled={disabled}
          cancelDisabled={disabled}
        />
      </View>
      <View style={styles.eventDetails}>
        <Text style={[styles.eventRowNameLabel]}>
          {props.displayName || 'All Schedules'}
          {' - '}
          <Text style={styles.eventRowNameLabelUnavailable}>
            {props.o.numProviders ? `${props.o.numProviders} Provider(s)` : 'Offline'}
          </Text>
        </Text>
      </View>
    </View>
  );
});

const ScheduledOverrides = memo(function ScheduledOverrides(props) {
  const selectDateKey = moment(props.selectedDate).format('YYYY-MM-DD');
  const timesForDate = props.overrides.eventsByTime[selectDateKey] || {};

  const confirmDialog = useContext(ConfirmDialogContext);

  const handleCancelPressConfirm = useCallback(
    (override) => {
      props.actions.deleteAppointmentSlotOverride({
        employerId: props.employerId,
        overrideId: override.overrideId,
      });
    },
    [props.actions, props.employerId]
  );
  const handleCancelPress = useCallback(
    (override) => {
      const confirmationOpts = {
        title: 'Remove this override?',
        confirm: 'Yes, Remove',
        reject: 'Cancel',
        onConfirmed: () => {
          handleCancelPressConfirm(override);
          confirmDialog.close();
        },
        onCancelled: () => {
          confirmDialog.close();
        },
      };
      confirmDialog.open(confirmationOpts);
    },
    [confirmDialog, handleCancelPressConfirm]
  );

  function renderOverride(o) {
    return (
      <OverrideRow
        key={o.overrideId}
        o={o}
        displayName={o.scheduleName}
        onCancelPress={handleCancelPress}
        canEdit={props.canEdit}
      />
    );
  }

  function renderEventTimeGroup(time) {
    const timeEvents = (timesForDate[time] || []).sort(function (a, b) {
      if (a.startDate < b.startDate) return -1;
      return 1;
    });

    return (
      <View key={`${time}`} style={styles.timeSection}>
        <View style={[styles.scheduledTextBubble, THEME.textBubbleSelected]}>
          <Text style={styles.textBubbleText}>{time}</Text>
        </View>
        <View style={{ width: '100%' }}>{timeEvents.map(renderOverride)}</View>
      </View>
    );
  }

  if (!Object.keys(timesForDate).length) return null;
  return (
    <View>
      <View style={styles.header}>
        <Text style={styles.headerText}>Provider Availability</Text>
      </View>
      <>{Object.keys(timesForDate).sort().map(renderEventTimeGroup)}</>
    </View>
  );
});

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    backgroundColor: COLOR.altContrast,
    borderBottomWidth: 1,
    borderColor: COLOR.altAccent,
    alignItems: 'center',
  },
  headerText: {
    flexGrow: 1,
    ...THEME.textSemibold,
    fontSize: 16,
    paddingVertical: 15,
    paddingHorizontal: 15,
    color: COLOR.text,
  },
  eventRow: __WEB__
    ? {
        flex: 1,
        ...THEME.basicRow,
      }
    : {
        ...THEME.basicRow,
      },
  eventDetails: {
    flex: 1,
  },
  eventRowNameLabel: {
    ...FONT.semibold,
    color: COLOR.text,
    fontSize: 16,
  },
  eventRowNameLabelUnavailable: {
    color: COLOR.textLight,
    fontSize: 14,
  },
  scheduledTextBubble: {
    ...THEME.textBubble,
    width: 120,
    height: 50,
    borderRadius: 30,
    justifyContent: 'center',
    marginRight: __WEB__ ? 30 : 10,
  },
  textBubbleText: {
    ...THEME.textBubbleText,
    fontSize: 20,
    letterSpacing: 1,
  },
  timeSection: {
    flexDirection: __WEB__ ? 'row' : 'column',
    alignItems: __WEB__ ? 'center' : 'flex-start',
    borderBottomWidth: 1,
    borderBottomColor: COLOR.altAccent,
    paddingLeft: 20,
    paddingBottom: 20,
    paddingTop: 10,
  },
});

function mapStateToProps(state) {
  const employer = getCurrentEmployer(state);

  return {
    employerId: employer?.employerId,
    deleteAptsSlotOverridesStatus: state.screens.scheduler.deleteAptsSlotOverridesStatus,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        deleteAppointmentSlotOverride,
      },
      dispatch
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ScheduledOverrides);
